import { useState, useEffect, useCallback, useRef, RefObject } from 'react'
import { useScrollContainer } from '@/components/shared/ScrollContext'

/**
 * Hook for collapsible page headers within MainLayout's scroll container.
 * Returns a `headerRef` to attach to the expanded header element.
 * `isMinimized` becomes true only when the header is fully scrolled out of view.
 */
export function useCollapsibleHeader(): {
  headerRef: RefObject<HTMLElement | null>
  isMinimized: boolean
} {
  const scrollRef = useScrollContainer()
  const headerRef = useRef<HTMLElement | null>(null)
  const [isMinimized, setIsMinimized] = useState(false)

  const handleScroll = useCallback(() => {
    const scrollEl = scrollRef?.current
    const headerEl = headerRef.current
    if (!scrollEl || !headerEl) return

    // Header is fully out of view when scrollTop >= header's bottom edge
    const headerBottom = headerEl.offsetTop + headerEl.offsetHeight
    setIsMinimized(scrollEl.scrollTop >= headerBottom)
  }, [scrollRef])

  useEffect(() => {
    const el = scrollRef?.current
    if (!el) return

    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [scrollRef, handleScroll])

  return { headerRef, isMinimized }
}
